import axios from "axios";
import { DataTable } from "simple-datatables";
import { showAlert, showConfirm, handleRequestError } from "../../utils/alert";
import VMasker from "vanilla-masker";

export default () => ({
  isModalOpen: false,
  isSubmitting: false,
  editMode: false,
  editId: null,

  plansList: [],
  globalPermissionsList: [],

  planForm: {
    name: "",
    max_users: "",
    features: [],
    prices: {
      MONTHLY: "",
      SEMIANNUALLY: "",
      YEARLY: "",
    },
  },

  plansDataTable: null,

  async initPlans() {
    const token = localStorage.getItem("admin_jwt_token");
    if (!token) {
      window.location.href = "/admin/login";
      return;
    }

    window.axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

    await this.fetchGlobalPermissions();
    await this.loadPlansTable();
  },

  async fetchGlobalPermissions() {
    try {
      const response = await axios.get("/api/admin/permissions");
      this.globalPermissionsList = response.data.data;
    } catch (error) {
      console.error("Failed to load global permissions dictionary.", error);
    }
  },

  // ================= FORMATTING HELPERS =================

  formatCurrency(value) {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  },

  applyMoneyMask(value) {
    if (!value) return "";

    // Se o valor vier do banco (ex: 150.00), converte para string e remove o ponto para o VMasker entender como 15000 centavos
    const stringValue =
      typeof value === "number" ? value.toFixed(2) : value.toString();
    const onlyNumbers = stringValue.replace(/\D/g, "");

    return VMasker.toMoney(onlyNumbers, {
      precision: 2,
      separator: ",",
      delimiter: ".",
      unit: "R$ ",
      zeroCents: false,
    });
  },

  unmaskMoney(maskedValue) {
    if (!maskedValue) return 0;
    const cleanString = maskedValue
      .toString()
      .replace("R$ ", "")
      .replace(/\./g, "")
      .replace(",", ".");
    return parseFloat(cleanString);
  },

  // Novo handler dinâmico para os inputs
  handlePriceInput(cycle, event) {
    this.planForm.prices[cycle] = this.applyMoneyMask(event.target.value);
  },

  // ================= TABELA =================

  async loadPlansTable() {
    try {
      const response = await axios.get("/api/admin/plans");
      this.plansList = response.data.data;

      if (this.plansDataTable) {
        this.plansDataTable.destroy();
      }

      const tbody = document.querySelector("#plansTable tbody");
      tbody.innerHTML = this.plansList
        .map((plan) => {
          const getPrice = (cycleName) => {
            const cycle = plan.prices?.find((p) => p.cycle === cycleName);
            return cycle ? this.formatCurrency(cycle.price) : "N/A";
          };

          return `
            <tr>
              <td class="py-4 px-4 xl:px-6">
                <p class="font-medium text-slate-800">${plan.name}</p>
                <p class="text-[10px] text-slate-500 uppercase tracking-widest">${plan.slug}</p>
              </td>
              <td class="py-4 px-4 xl:px-6 font-medium text-slate-600 text-sm">
                <div class="flex flex-col gap-1">
                    <span><strong class="text-xs">1x:</strong> ${getPrice("MONTHLY")}</span>
                    <span><strong class="text-xs">6x:</strong> ${getPrice("SEMIANNUALLY")}</span>
                    <span><strong class="text-xs">12x:</strong> ${getPrice("YEARLY")}</span>
                </div>
              </td>
              <td class="py-4 px-4 xl:px-6">
                <span class="inline-flex rounded-full px-3 py-1 text-xs font-medium ${plan.max_users === null ? "bg-green-100 text-green-800" : "bg-slate-100 text-slate-800"}">
                  ${plan.max_users === null ? "Ilimitado" : plan.max_users + " Usuários"}
                </span>
              </td>
              <td class="text-left py-4 px-4 xl:px-6">
                <div class="flex justify-start gap-2">
                  <button
                    onclick="window.dispatchEvent(new CustomEvent('edit-plan', {detail: ${plan.id}}))"
                    class="inline-flex items-center justify-center rounded-md bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-orange-600 focus:outline-none"
                  >
                    Editar
                  </button>
                  <button
                    onclick="window.dispatchEvent(new CustomEvent('delete-plan', {detail: ${plan.id}}))"
                    class="inline-flex items-center justify-center rounded-md bg-red-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-red-700 focus:outline-none"
                  >
                    Excluir
                  </button>
                </div>
              </td>
            </tr>
          `;
        })
        .join("");

      this.plansDataTable = new DataTable("#plansTable", {
        searchable: false,
        perPageSelect: false,
        labels: {
          info: "Mostrando de {start} até {end} de {rows} registos",
          noRows: "Nenhum plano cadastrado",
          noResults: "Nenhum resultado encontrado",
        },
      });
    } catch (error) {
      console.error(error);
    }
  },

  // ================= MODAIS =================

  openCreatePlan() {
    this.editMode = false;
    this.planForm = {
      name: "",
      max_users: "",
      features: [],
      prices: { MONTHLY: "", SEMIANNUALLY: "", YEARLY: "" },
    };
    this.isModalOpen = true;
  },

  openEditPlan(id) {
    this.editMode = true;
    this.editId = id;
    const plan = this.plansList.find((p) => p.id === id);

    const getMaskedPrice = (cycleName) => {
      const cycle = plan.prices?.find((p) => p.cycle === cycleName);
      // Aplica a máscara no valor cru que vem do banco para exibir bonito no formulário
      return cycle ? this.applyMoneyMask(cycle.price) : "";
    };

    this.planForm = {
      name: plan.name,
      max_users: plan.max_users === null ? "" : plan.max_users,
      features: plan.features || [],
      prices: {
        MONTHLY: getMaskedPrice("MONTHLY"),
        SEMIANNUALLY: getMaskedPrice("SEMIANNUALLY"),
        YEARLY: getMaskedPrice("YEARLY"),
      },
    };
    this.isModalOpen = true;
  },

  // ================= SUBMIT =================

  async submitPlan() {
    this.isSubmitting = true;
    try {
      const payload = {
        ...this.planForm,
        max_users:
          this.planForm.max_users === ""
            ? null
            : parseInt(this.planForm.max_users),

        // Remove as máscaras antes de enviar para a API (transformando "R$ 150,00" em 150.00)
        prices: {
          MONTHLY: this.unmaskMoney(this.planForm.prices.MONTHLY),
          SEMIANNUALLY: this.unmaskMoney(this.planForm.prices.SEMIANNUALLY),
          YEARLY: this.unmaskMoney(this.planForm.prices.YEARLY),
        },
      };

      if (this.editMode) {
        await axios.put(`/api/admin/plans/${this.editId}`, payload);
        showAlert("Plano atualizado com sucesso!", "success");
      } else {
        await axios.post("/api/admin/plans", payload);
        showAlert("Plano criado com sucesso!", "success");
      }

      await this.loadPlansTable();
      this.isModalOpen = false;
    } catch (error) {
      handleRequestError(error, "Erro ao guardar o plano.");
    } finally {
      this.isSubmitting = false;
    }
  },

  async deletePlan(id) {
    const result = await showConfirm(
      "Excluir Plano",
      "Tem certeza que deseja excluir este plano? Esta ação não pode ser desfeita.",
    );

    if (result.isConfirmed) {
      try {
        await axios.delete(`/api/admin/plans/${id}`);
        await this.loadPlansTable();
        showAlert("Plano removido com sucesso.", "success");
      } catch (error) {
        handleRequestError(error, "Erro ao excluir o plano.");
      }
    }
  },
});
